import React, { useState } from 'react';
import { Phone, Mail, Shield, Lock, Cookie, ChevronDown } from 'lucide-react';
import { doctorInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [openSection, setOpenSection] = useState(null);

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${doctorInfo.whatsapp}?text=${encodeURIComponent(doctorInfo.whatsappMessage)}`, '_blank');
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const legalSections = [
    {
      id: 'legal',
      icon: Shield,
      title: 'Aviso Legal',
      content: 'El titular de esta web es el Dr. Carlos Sanz Martín, médico colegiado nº 47405674 en el Colegio de Médicos de Valladolid. La actividad sanitaria se rige por la Ley de Ordenación de Profesiones Sanitarias.'
    },
    {
      id: 'privacy',
      icon: Lock,
      title: 'Política de Privacidad',
      content: 'De acuerdo con el RGPD, los datos personales y de salud facilitados durante la consulta o contacto serán tratados con estricta confidencialidad bajo secreto médico, con la única finalidad de prestar asistencia sanitaria y gestionar la relación médico-paciente.'
    },
    {
      id: 'cookies',
      icon: Cookie,
      title: 'Cookies',
      content: 'Este sitio utiliza solo cookies técnicas necesarias para el funcionamiento del servicio.'
    }
  ];

  return (
    <footer id="contacto" className="bg-gradient-to-b from-[#3D2418] to-[#2a1910] text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#7DAAAF]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#7DAAAF]/5 rounded-full blur-3xl"></div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Contact Info */}
          <div>
            <div className="flex items-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_doctor-visit-6/artifacts/m46hav3g_Captura%20de%20pantalla%202026-02-25%20a%20las%2022.15.22.png" 
                alt="Logo" 
                className="h-16 w-auto object-contain"
              />
              <div className="ml-4">
                <h3 className="text-xl font-bold text-white">{doctorInfo.name}</h3>
                <p className="text-[#7DAAAF] text-sm">{doctorInfo.collegiateNumber}</p>
              </div>
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed">
              {doctorInfo.specialty}
            </p>
            <p className="text-gray-400 text-sm">
              Valladolid
            </p>
          </div>

          {/* Contact Methods */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">Contacto</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${doctorInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center text-gray-300 hover:text-[#7DAAAF] transition-colors group"
                >
                  <div className="w-10 h-10 bg-[#7DAAAF]/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-[#7DAAAF] transition-colors">
                    <Phone className="w-5 h-5 text-[#7DAAAF] group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Teléfono</p>
                    <p className="font-medium">{doctorInfo.phone}</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${doctorInfo.email}`}
                  className="flex items-center text-gray-300 hover:text-[#7DAAAF] transition-colors group"
                >
                  <div className="w-10 h-10 bg-[#7DAAAF]/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-[#7DAAAF] transition-colors">
                    <Mail className="w-5 h-5 text-[#7DAAAF] group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <p className="font-medium text-sm break-all">{doctorInfo.email}</p>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Info Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">Información Legal</h4>
            <ul className="space-y-3">
              {legalSections.map((section) => {
                const Icon = section.icon;
                return (
                  <li key={section.id}>
                    <button 
                      onClick={() => toggleSection(section.id)}
                      className="flex items-center text-gray-300 hover:text-[#7DAAAF] transition-colors text-left group w-full"
                    >
                      <Icon className="w-5 h-5 text-[#7DAAAF] mr-2 flex-shrink-0" />
                      <span className="text-sm flex-1">{section.title}</span>
                      <ChevronDown 
                        className={`w-4 h-4 text-[#7DAAAF] transition-transform duration-300 ${
                          openSection === section.id ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Collapsible Legal Text Sections */}
        <div className="border-t border-gray-700 pt-8 space-y-4">
          {legalSections.map((section) => {
            const Icon = section.icon;
            return (
              <div 
                key={section.id}
                className={`bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden transition-all duration-300 ${
                  openSection === section.id ? 'max-h-96' : 'max-h-0 border-transparent'
                }`}
              >
                {openSection === section.id && (
                  <div className="p-6 animate-in">
                    <h5 className="font-bold text-white mb-3 flex items-center">
                      <Icon className="w-5 h-5 text-[#7DAAAF] mr-2" />
                      {section.title}
                    </h5>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <button 
        onClick={handleWhatsApp}
        data-testid="whatsapp-floating-btn"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300 group"
        aria-label="Contactar por WhatsApp"
      >
        <Phone className="w-7 h-7 text-white" />
        <span className="absolute right-20 bg-white text-gray-800 px-4 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-sm font-medium">
          ¿Necesitas ayuda?
        </span>
      </button>

      {/* Copyright Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-400 text-sm">
            © {currentYear} Dr. Carlos Sanz Martín. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
